import type { RootState } from "../../redux";
import { AdminEndpoints, Subdomains } from "../../constants";
import type { User, Images, LoginData, DashboardState, ClientToServerEvents, ServerToClientEvents } from "../../types";
import {
  getUsers,
  searchUserByRegNum,
  loginAdmin,
  resetCount,
  logoutAdmin,
  getTotalUsers,
  getSingleUser,
  getUserImages,
  changePassword,
  loginReturningAdmin,
  deleteAttempsAPI,
  getAllRolesAPI,
  addNewUserAPI,
  switchUserStatusAPI,
  searchUserByIsaeliId,
} from "../../functions";
import { isAxiosError } from "axios";
import type { Socket } from "socket.io-client";
import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { NewUser } from "../AdminPanel/NewUser";

const initialState: DashboardState = {
  role: null,
  username: null,
  users: [],
  filteredUsers: [],
  numberOfEntries: null,
  images: null,
  searchedUser: null,
  savedSocket: null,
  entriesForReview: [],
  paneemTotalUsers: [],
  portalTotalUsers: [],
  permissions: null,
  roles: null,
};

export const addNewUserAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.newUser}`,
  async (user: NewUser) => {
    const response = await addNewUserAPI(user);

    return response.data;
  }
);

export const getAllRolesAction = createAsyncThunk(`${Subdomains.admins}${AdminEndpoints.getRoles}`, async () => {
  const response = await getAllRolesAPI();

  return response.data;
});

export const deleteAttempsAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.delete}`,
  async (user: User) => {
    const response = await deleteAttempsAPI(user);

    return response.data;
  }
);
export const switchUserStatus: any = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.switchStatus}`,
  async (user: User) => {
    const response = await switchUserStatusAPI(user.registrationNumber);

    return response.data;
  }
);

export const loginReturningAdminAction = createAsyncThunk(`${Subdomains.admins}${AdminEndpoints.reLogIn}`, async () => {
  const response = await loginReturningAdmin();

  return response.data;
});

export const loginAdminAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.login}`,
  async (data: LoginData, { rejectWithValue }) => {
    try {
      const response = await loginAdmin(data);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.data.message);
      else rejectWithValue(error.message);
    }
  }
);

export const logoutAdminAction = createAsyncThunk(`${Subdomains.admins}${AdminEndpoints.logout}`, async () => {
  const response = await logoutAdmin();

  return response.data;
});

export const getUsersAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.allEntries}`,
  async (page: string, { rejectWithValue }) => {
    try {
      const response = await getUsers(page);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const getSingleUserAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.singleEntry}`,
  async (registrationNumber: string, { rejectWithValue }) => {
    try {
      const response = await getSingleUser(registrationNumber);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const getUserImagesAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.images}`,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getUserImages(id);

      return response.data.imageData;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const searchUserByRegNumAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.searchByRegNum}`,
  async (registrationNumber: string, { rejectWithValue }) => {
    try {
      const response = await searchUserByRegNum(registrationNumber);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const searchUserByIsraeliIdAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.searchByIsraeliId}`,
  async (israeliId: string, { rejectWithValue }) => {
    try {
      const response = await searchUserByIsaeliId(israeliId);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const resetCountAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.countReset}`,
  async (registrationNumber: number, { rejectWithValue }) => {
    try {
      const response = await resetCount(registrationNumber);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const changePasswordAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.passwordChange}`,
  async ({ currentPassword, password }: { currentPassword: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await changePassword(currentPassword, password);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const getTotalUsersAction = createAsyncThunk(
  `${Subdomains.admins}${AdminEndpoints.totals}`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTotalUsers();

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.status);
      else rejectWithValue(error.message);
    }
  }
);

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearFilteredUsers: (state) => {
      state.filteredUsers = [];
    },

    setImages: (state, { payload }: { payload: Images }) => {
      state.images = payload;
    },

    clearImages: (state) => {
      state.images = null;
    },

    setSocket: (state, { payload }: { payload: Socket<ServerToClientEvents, ClientToServerEvents> | null }) => {
      //@ts-ignore
      state.savedSocket = payload;
    },

    setEntriesForReview: (state, { payload }: { payload: User[] }) => {
      state.entriesForReview = payload;
    },
  },

  extraReducers(builder) {
    builder.addCase(getAllRolesAction.fulfilled, (state, { payload }) => {
      state.roles = payload.roles;
    });
    builder.addCase(deleteAttempsAction.fulfilled, (state, { payload }) => {
      state.filteredUsers = [];
    });
    builder.addCase(getUsersAction.fulfilled, (state, { payload }) => {
      state.users = payload!.users;
      state.numberOfEntries = payload!.numberOfEntries;
    });

    builder.addCase(getSingleUserAction.fulfilled, (state, { payload }) => {
      state.filteredUsers = payload!.users;
    });

    builder.addCase(getUserImagesAction.fulfilled, (state, { payload }) => {
      state.images = {
        selfie: payload!.selfie,
        document: payload!.document,
        documentBack: payload!.documentBack,
        selfieRef: payload!.selfieRef,
      };
    });

    builder.addCase(searchUserByRegNumAction.fulfilled, (state, { payload }) => {
      state.searchedUser = payload!.user;
    });
    builder.addCase(searchUserByIsraeliIdAction.fulfilled, (state, { payload }) => {
      state.searchedUser = payload!.user;
    });

    builder.addCase(getTotalUsersAction.fulfilled, (state, { payload }) => {
      state.paneemTotalUsers = payload!.paneemUsers;
      state.portalTotalUsers = payload!.portalUsers;
    });

    builder.addMatcher(isAnyOf(logoutAdminAction.fulfilled, logoutAdminAction.rejected), (state) => {
      state.role = null;
      state.username = null;
      state.users = [];
      state.filteredUsers = [];
      state.numberOfEntries = null;
      state.images = null;
      state.searchedUser = null;
      state.savedSocket = null;
      state.entriesForReview = [];
      state.paneemTotalUsers = [];
      state.portalTotalUsers = [];
    });

    builder.addMatcher(
      isAnyOf(
        getUsersAction.rejected,
        getUserImagesAction.rejected,
        resetCountAction.rejected,
        changePasswordAction.rejected,
        getSingleUserAction.rejected,
        searchUserByRegNumAction.rejected,
        searchUserByIsraeliIdAction.rejected,
        getTotalUsersAction.rejected
      ),
      (state, { payload }) => {
        if (payload === 401) {
          state.role = null;
          state.username = null;
          state.users = [];
          state.filteredUsers = [];
          state.numberOfEntries = null;
          state.images = null;
          state.searchedUser = null;
          state.savedSocket = null;
          state.entriesForReview = [];
          state.paneemTotalUsers = [];
          state.portalTotalUsers = [];
        }
      }
    );

    builder.addMatcher(
      isAnyOf(loginReturningAdminAction.fulfilled, loginAdminAction.fulfilled),
      (state, { payload }) => {
        state.role = payload!.role;
        state.username = payload!.username;
        state.permissions = payload!.permissions;
      }
    );
  },
});

export const { clearFilteredUsers, clearImages, setImages, setSocket, setEntriesForReview } = dashboardSlice.actions;

export const selectRole = (state: RootState) => state.dashboard.role;
export const selectUsername = (state: RootState) => state.dashboard.username;
export const selectUsers = (state: RootState) => state.dashboard.users;
export const selectFilteredUsers = (state: RootState) => state.dashboard.filteredUsers;
export const selectNumberOfEntries = (state: RootState) => state.dashboard.numberOfEntries;
export const selectImages = (state: RootState) => state.dashboard.images;
export const selectSearchedUser = (state: RootState) => state.dashboard.searchedUser;
export const selectSocket = (state: RootState) => state.dashboard.savedSocket;
export const selectEntriesForReview = (state: RootState) => state.dashboard.entriesForReview;
export const selectPaneemTotalUsers = (state: RootState) => state.dashboard.paneemTotalUsers;
export const selectPortalTotalUsers = (state: RootState) => state.dashboard.portalTotalUsers;

export const selectPermissions = (state: RootState) => state.dashboard.permissions;

export const selectRoles = (state: RootState) => state.dashboard.roles;

export default dashboardSlice.reducer;
